import type { Insight } from '../types';

export const insightsTr: Insight[] = [
  {
    id: "insight-1",
    slug: "halka-arz-reklam-filmi-stratejisi-yatirimci-guveni",
    title: "Halka Arz ve Finansal Kampanyalarda Sinematik Reklam Filminin Yatırımcı Güvenine Etkisi",
    metaTitle: "Halka Arz Reklam Filmi Stratejisi ve Yatırımcı Güveni - WE24 Agency",
    metaDescription: "Halka arz (IPO) ve büyük finansal kampanyalarda sinematik reklam filmlerinin, özel bestelenen özgün müziklerin ve TV/dijital dağıtımın yatırımcı güvenine etkisi.",
    excerpt: "Büyük ölçekli halka arzlarda yatırımcı kararlarını yalnızca finansal tablolar değil, markanın kurumsal vizyonunu aktaran sinematik güven dili belirler. Alfa Solar vakamızdan edindiğimiz stratejik içgörüler.",
    category: "Strateji",
    readTime: "5 dk okuma",
    publishDate: "2024-02-10",
    author: {
      name: "Harutyun Arto Davulciyan & Aren Arda Kaya",
      role: "Kreatif Direktör & Strateji Masası"
    },
    coverImage: "/project-covers/alfa-solar.webp",
    featured: true,
    tags: ["Halka Arz", "Reklam Filmi", "Finansal İletişim", "Alfa Solar", "Marka Stratejisi", "TV Prodüksiyonu"],
    relatedProjectSlugs: ["1351", "fabrika-tanitim-filmi-sapro-kurumsal-tanitim-filmi-we24-agency"],
    relatedServiceSlugs: ["video-produksiyon", "reklam-filmi"],
    sections: [
      {
        heading: "1. Finansal Verilerin Ötesi: Yatırımcı Psikolojisi ve Güven İnşası",
        subheading: "Rakamları Hikayeye, Bilançoları Gelecek Vizyonuna Dönüştürmek",
        paragraphs: [
          "Bir şirketin halka arz (IPO) süreci, yalnızca Sermaye Piyasası Kurulu (SPK) onayları ve izahname bültenlerinden ibaret değildir. Halka arz, markanın kurucularından üretim gücüne, Ar-Ge vizyonundan sürdürülebilirlik ilkelerine kadar tüm varlığını kamuoyuna sunduğu en kritik dönüm noktasıdır.",
          "Bireysel ve kurumsal yatırımcılar, yatırım kararı verirken bilançolar kadar markanın 'büyüme kararlılığına' ve 'yönetim kalitesine' bakarlar. İşte bu noktada 4K sinematik prodüksiyonla kurgulanmış bir reklam filmi, şirketin devasa üretim altyapısını ve güvenilirliğini saniyeler içinde zihinlere nakşeder."
        ],
        callout: {
          title: "Vaka İspatı (Alfa Solar)",
          text: "Alfa Solar'ın tarihi halka arz sürecinde hazırladığımız reklam filmi; özel bestelenen özgün müziği, güneş paneli üretim sahneleri ve TV/dijital eşzamanlı yayınlarıyla yüz binlerce yatırımcıda güven uyandırdı ve tarihi talep rekoru kırılmasına katkı sağladı.",
          type: "stat"
        }
      },
      {
        heading: "2. Özgün Bestelenmiş Müziğin Bilinçaltı Gücü",
        subheading: "Stok Müziklerin Yarattığı Güvensizliği Sıfırlamak",
        paragraphs: [
          "Büyük bütçeli bir finansal kampanyada hazır stok müzik kullanmak, markanın 'sıradan' algılanmasına yol açar. Halka arz filmlerinde müzik, görselle tam senkron bir dinamizm taşımalı, prestij ve coşku hissini aynı anda tetiklemelidir.",
          "Alfa Solar reklam filmimizde ajansımızın müzisyeni Avedis Özdemir tarafından bestelenen ve WE24 ses stüdyolarında canlı enstrümanlarla kaydedilen özgün tema müziği, reklamın hafızalarda yer etmesini sağlayan en belirleyici unsur oldu."
        ],
        bulletPoints: [
          "Canlı orkestrasyon ve modern synth katmanlarıyla güçlendirilmiş güven teması",
          "TV reklam kuşaklarında anında fark edilen imza ses kimliği (Audio Branding)",
          "Dijital mecralarda akılda kalıcılığı artıran ritmik dinamizm"
        ]
      },
      {
        heading: "3. TV ve Dijital Mecra Çıktılarının Entegre Dağıtımı",
        subheading: "Her Ekrana Özel Kadrajlama ve Mesaj Hiyerarşisi",
        paragraphs: [
          "Ulusal TV kanallarında 16:9 yayınlanan master reklam filminin yanı sıra, dijital yatırımcı platformları ve sosyal medya için 9:16 dikey formatlar, 6 saniyelik bumper videolar ve yatırımcı odaklı teaser içerikler aynı prodüksiyon setinde eşzamanlı olarak üretilmelidir.",
          "Böylece televizyonun kitlesel prestiji ile dijital medyanın doğrudan hedefleme gücü tek bir yaratıcı dilde birleştirilir."
        ]
      }
    ],
    checklist: {
      title: "Halka Arz Reklam Filmi İçin Başarı Kriterleri",
      items: [
        "Üretim ve Ar-Ge gücünü somutlaştıran gerçek 4K fabrika çekimleri",
        "Markaya özel tescilli özgün müzik ve profesyonel ses tasarımı",
        "Yasal uyarı ve izinlerin TV/dijital mevzuatına tam uyumu",
        "TV (16:9), YouTube (16:9) ve Sosyal Medya (9:16 / 4:5) formatlarının eşzamanlı teslimatı",
        "Yatırımcı psikolojisini harekete geçiren güçlü ve net bir ana mesaj"
      ]
    },
    faqs: [
      {
        question: "Halka arz reklam filmi ne zaman yayına girmelidir?",
        answer: "Genellikle SPK onayının ardından talep toplama tarihlerinden 1-2 hafta önce başlayarak talep toplama sürecinin son gününe kadar yoğun bir frekansta yayında kalması tavsiye edilir."
      },
      {
        question: "Bir halka arz filminin prodüksiyon süresi ne kadardır?",
        answer: "Senaryo geliştirme, fabrika/ofis çekimleri, özgün müzik bestelenmesi ve post-prodüksiyon dahil olmak üzere ideal süreç 3 ila 4 haftadır."
      }
    ]
  },
  {
    id: "insight-2",
    slug: "sifir-hata-canli-yayin-rejisi-led-ekran-kablosuz-kamera-vtr",
    title: "Büyük Ölçekli Etkinlik ve Ödül Törenlerinde Sıfır Hata Canlı Yayın Rejisi Nasıl Kurgulanır?",
    metaTitle: "Sıfır Hata Canlı Yayın Rejisi, LED Ekran & Kablosuz Kamera Mimarisi - WE24",
    metaDescription: "Kristal Piksel ve Talesworld lansmanlarından teknik içgörüler: Çok kameralı reji, dev LED ekran mapping, VTR sistemleri ve sıfır gecikmeli kablosuz kamera entegrasyonu.",
    excerpt: "Ödül törenleri ve lansmanlarda geri sarma şansınız yoktur. Dev LED ekran senkronizasyonundan VTR kaynaklarına, yedekli reji mimarisinden sıfır gecikmeli kablosuz hareketli kameralara canlı yayının anatomisi.",
    category: "Canlı Yayın",
    readTime: "6 dk okuma",
    publishDate: "2024-03-05",
    author: {
      name: "Harutyun Arto Davulciyan & WE24 Reji Ekibi",
      role: "Canlı Yayın & Sahne Yönetmenliği"
    },
    coverImage: "/project-covers/kristal-piksel-2025-canli-yayin.webp",
    featured: true,
    tags: ["Canlı Yayın", "Reji Masası", "LED Ekran", "Kablosuz Kamera", "VTR", "Kristal Piksel", "Bannerlord", "Esports"],
    relatedProjectSlugs: ["kristal-piksel-2025-canli-yayin", "launch-day-stream-mount-blade-ii-bannerlord-launch-day-stream-canli-yayin"],
    relatedServiceSlugs: ["canli-yayin", "video-produksiyon"],
    sections: [
      {
        heading: "1. Canlı Yayının Altın Kuralı: Redundant (Yedekli) Sinyal Altyapısı",
        subheading: "Asla Tek Bir Kabloya veya Tek Bir Switcher'a Güvenmeyin",
        paragraphs: [
          "Büyük ölçekli bir ödül töreninde (Örn: TOGED Kristal Piksel Video Oyun Ödülleri) veya global bir oyun lansmanında (Örn: TaleWorlds Mount & Blade II: Bannerlord) yüz binlerce izleyici ekran başındayken en ufak bir sinyal kesintisi telafisi olmayan bir marka krizine dönüşebilir.",
          "Profesyonel reji mimarisinde ana yayın switcher'ının yanı sıra birebir senkron çalışan bir Backup (Yedek) Switcher ve çift bağımsız internet hattı (Bonded Cellular + Dedicated Fiber) devrede olmalıdır."
        ]
      },
      {
        heading: "2. Kablosuz Hareketli Kameraların Sıfır Gecikmeyle (Zero-Latency) Rejiye Girişi",
        subheading: "Gimbal, Steadicam ve Sahne İçi Dinamik Planlar",
        paragraphs: [
          "Statik tripod kameralar artık modern izleyiciyi tatmin etmiyor. Ödül kazananların sahneye çıkış anını, salondaki coşkuyu ve sahne arkası atmosferi aktarmak için kablosuz hareketli kameralar (Wireless Gimbal & Steadicam) şarttır.",
          "Ancak kablosuz sistemlerde en büyük risk 'gecikme (latency)' ve 'sinyal enterferansı'dır. WE24 canlı yayın altyapısında kullandığımız profesyonel DFS özellikli RF kablosuz verici sistemleri sayesinde, hareketli kameralardan gelen 10-bit 4:2:2 uncompressed sinyal 1 milisaniyenin altında gecikmeyle (Near-Zero Latency) doğrudan reji masasına ve dev LED ekranlara input edilir. Böylece sahnede konuşan kişi ile arkasındaki dev ekranda dudak senkronu kusursuz kalır."
        ],
        callout: {
          title: "Teknik Standart",
          text: "Sahne içi kablosuz kamera sinyalleri, doğrudan Vision Mixer üzerindeki Genlock senkronlu SDI girişlere aktarılır. Bu sayede canlı yayın ile dev sahne LED ekranları arasında en ufak bir 'frame drop' veya 'lip-sync' kayması yaşanmaz.",
          type: "tip"
        }
      },
      {
        heading: "3. Dev LED Ekran Mapping ve Dinamik VTR Video Oynatım Sistemleri",
        subheading: "Sahne Arkası Görsel Şov ile Canlı Yayın Akışının Uyumu",
        paragraphs: [
          "Sahnede yer alan yüzlerce metrekarelik dev LED ekranlar yalnızca bir arka plan değildir; sunum anında aday videolarını (VTR), kazanan anons grafiklerini ve dinamik ışık efektlerini yansıtan canlı bir aktördür.",
          "Çok kanallı profesyonel VTR sunucuları (Video Playback Servers) sayesinde; canlı yayına giren aday videosu ile sahnedeki LED ekrana basılan içerik tek bir tetikleme (cue) ile milisaniyesi milisaniyesine senkronize oynatılır."
        ],
        bulletPoints: [
          "Aday tanıtım VTR'lerinin anında ana ekrana ve yayın sinyaline eşzamanlı yönlendirilmesi",
          "Ödül anonsu anında dinamik LED arka plan grafiklerinin canlı tetiklenmesi",
          "Canlı yayın alt yazı ve alt bant (Lower Third) grafiklerinin gerçek zamanlı CG yazılımlarıyla oluşturulması"
        ]
      },
      {
        heading: "4. Eşzamanlı VOD Kayıt ve Sosyal Medya İçerik Fabrikası",
        subheading: "Yayın Devam Ederken Sosyal Medyayı Ele Geçirmek",
        paragraphs: [
          "Etkinlik sürerken her kamera açısı bağımsız olarak (ISO Recording) yüksek bit-rate ProRes formatında kaydedilir. Canlı yayın devam ederken reji arkasındaki montaj masamız kazananların anons anlarını 60 saniye içinde dikey video (Reels/Shorts) olarak kurgulayıp sosyal medya ekiplerine teslim eder."
        ]
      }
    ],
    checklist: {
      title: "Kusursuz Canlı Yayın Rejisi Kontrol Listesi",
      items: [
        "Ana ve yedek yayın mikserlerinin (Dual Switcher) tam senkronizasyonu",
        "Kablosuz hareketli kameralarda ultra düşük gecikmeli (Zero-Latency) RF bağlantı testi",
        "Dev LED ekran çözünürlük mapping ve VTR sunucu cue provaları",
        "Tüm kameraların renk kalibrasyonu (Camera Shading & Multi-Cam White Balance)",
        "Her kamera için bağımsız ISO ProRes master kayıt düzeni",
        "Reji ekibi, ses mühendisi ve sahne amiri arasında kesintisiz Intercom iletişimi"
      ]
    },
    faqs: [
      {
        question: "Canlı yayında internet kesilirse ne olur?",
        answer: "Kullandığımız 4G/5G çoklu hat birleştirici (Bonded Cellular) ve karasal fiber hat yedekliliği sayesinde hatlardan biri kopsa dahi yayın kesintisiz olarak diğer hat üzerinden akmaya devam eder."
      },
      {
        question: "Aynı anda hem YouTube'a hem de Twitch'e canlı yayın yapılabilir mi?",
        answer: "Evet. Donanımsal çoklu encoder altyapımız ile aynı reji sinyali YouTube, Twitch, LinkedIn, X ve özel web portallarına eşzamanlı ve bağımsız bitrate ayarlarıyla iletilir."
      }
    ]
  },
  {
    id: "insight-3",
    slug: "agir-sanayi-fabrika-tanitim-filmi-b2b-sinematografi",
    title: "Ağır Sanayi ve Fabrika Tanıtım Filmlerinde Sinematik Estetik: Demirin ve Üretimin Gücü",
    metaTitle: "Ağır Sanayi ve Fabrika Tanıtım Filmi Sinematografisi - WE24 Agency",
    metaDescription: "KUTES ve Sapro vaka deneyimleriyle endüstriyel video prodüksiyonu: 1500°C erimiş demir çekimleri, 4K 120FPS yüksek hız ve global B2B ihracatta güven inşası.",
    excerpt: "Klasik ve durağan fabrika videoları dönemi kapandı. 1500°C erimiş maden dökümlerinden 4K 120FPS ultra yavaş çekimlere, endüstriyel foley ses tasarımından küresel B2B ihracat pazarlarına sinematik üretim dili.",
    category: "Prodüksiyon",
    readTime: "5 dk okuma",
    publishDate: "2024-04-12",
    author: {
      name: "Harutyun Arto Davulciyan & Görüntü Departmanı",
      role: "Yönetmen & Endüstriyel Sinematografi"
    },
    coverImage: "/project-covers/fabrika-tanitim-filmi-kutes.webp",
    featured: true,
    tags: ["Fabrika Tanıtım Filmi", "Endüstriyel Prodüksiyon", "KUTES", "Sapro", "B2B Pazarlama", "4K Sinematografi", "Ses Tasarımı"],
    relatedProjectSlugs: ["fabrika-tanitim-filmi-kutes-we24-agency-fabrika-reklam-filmi", "fabrika-tanitim-filmi-sapro-kurumsal-tanitim-filmi-we24-agency", "dicle-tekstil-fabrika-tanitim-filmi-we24-agency-kurumsal-film"],
    relatedServiceSlugs: ["tanitim-filmi", "video-produksiyon"],
    sections: [
      {
        heading: "1. Fabrika Filmlerinde Paradigma Değişimi: Belgesel Değil, Sinema",
        subheading: "Monoton Gezinti Videolarından Yüksek Tempolu Endüstriyel Başyapıtlara",
        paragraphs: [
          "Geleneksel fabrika tanıtım filmleri genellikle binanın dış çekimiyle başlayan, ardından çalışanların yanından geçen tekdüze bir kamera gezintisinden ibarettir. Ancak bugün Almanya'dan Amerika'ya küresel B2B satın alma heyetleri, tedarikçi seçerken bir fabrikanın ne kadar modern, titiz ve teknolojik olduğunu hissetmek istiyor.",
          "KUTES Döküm fabrikası için hazırladığımız tanıtım filminde, dökümhanenin zorlu fiziksel koşullarını sinematik bir görsel avantaja dönüştürdük. 1500°C sıcaklıktaki erimiş demirin potadan kalıba akışını yüksek dinamik aralıklı (HDR) sinema kameralarıyla kaydettik."
        ]
      },
      {
        heading: "2. Yüksek Hızlı Çekim (High-Speed 120FPS) ve Makro Optiklerin Büyüsü",
        subheading: "Gözle Görülemeyen Zanaat ve Mühendislik Detaylarını Görünür Kılmak",
        paragraphs: [
          "Endüstriyel robot kolların milisaniyelik hareketleri, talaşlı imalat esnasında soğutma sıvısının püskürmesi ve pres makinelerinin devasa gücü saniyede 120 kare (120FPS) yavaşlatıldığında adeta hipnotize edici bir sanat eserine dönüşür.",
          "Makro sinema lensleri kullanarak mikron seviyesindeki parça işleme kalitesini ve yüzey hassasiyetini ekrana taşıyoruz. Bu görsel kanıt, B2B alıcı nezdinde şirketin kalite kontrol standartlarına duyulan güveni zirveye çıkarır."
        ],
        callout: {
          title: "Prodüksiyon Notu",
          text: "Endüstriyel tesislerdeki floresan ve cıvalı aydınlatmaların oluşturduğu renk kirliliği, sahada kurulan yüksek CRI sinema ışıklarıyla dengelenir; post-prodüksiyonda ise metalin soğuk tonları ile erimiş demirin akkor ateşi arasında dramatik bir renk kontrastı oluşturulur.",
          type: "quote"
        }
      },
      {
        heading: "3. Endüstriyel Foley Ses Tasarımı: Filmin Ruhunu Oluşturan Güç",
        subheading: "Makinelerin Sesini Bir Senfoniye Dönüştürmek",
        paragraphs: [
          "Görsel ne kadar kusursuz olursa olsun, arkasında sadece jenerik bir kurumsal müzik çalıyorsa o film duyguyu geçiremez. Fabrika çekimlerinde yönlü mikrofonlarla kaydettiğimiz hidrolik pres vuruşları, lazer kesim cızırtıları ve metal çınlamaları stüdyomuzda 'Endüstriyel Foley Ses Tasarımı' olarak yeniden inşa edilir.",
          "İzleyici, dökülen demirin ağırlığını ve presin gücünü kulaklarında hisseder."
        ]
      }
    ],
    checklist: {
      title: "Endüstriyel Fabrika Çekiminde Dikkat Edilecek Unsurlar",
      items: [
        "İş güvenliği ve İSG kurallarına tam uyumlu kamera ve ışık ekipman rotası",
        "Yüksek sıcaklık ve tozlu ortamlara dayanıklı sinema kamera koruma ekipmanları",
        "4K 120FPS / 60FPS yüksek kare hızlarıyla hareketlerin estetik yavaşlatılması",
        "Tesisin temiz, düzenli ve modern üretim hatlarını vurgulayan kadraj disiplini",
        "Uluslararası fuar ve B2B görüşmelere uygun çok dilli (İngilizce/Almanca) seslendirme"
      ]
    },
    faqs: [
      {
        question: "Fabrikada üretim devam ederken çekim yapılabilir mi?",
        answer: "Evet. Prodüksiyon planlamamız fabrika vardiya düzenini aksatmayacak şekilde İSG biriminizle koordineli olarak yürütülür ve hatlar durdurulmadan gerçek üretim akışı kaydedilir."
      },
      {
        question: "Fabrika tanıtım filmi kaç dakika olmalıdır?",
        answer: "Global B2B standartlarında web sitesi ve fuarlar için ideal ana film süresi 2.5 - 3.5 dakika arasındadır. Ayrıca sosyal medya için 30 ve 60 saniyelik dinamik kesitler hazırlanır."
      }
    ]
  },
  {
    id: "insight-4",
    slug: "etkili-tanitim-filmi-brief-nasil-hazirlanir-sablon",
    title: "Etkili Bir Tanıtım Filmi İçin Kusursuz Brief Nasıl Hazırlanır? (Ajans Rehberi & Kontrol Listesi)",
    metaTitle: "Tanıtım Filmi Briefi Nasıl Hazırlanır? Şablon ve Rehber - WE24 Agency",
    metaDescription: "Kurumsal tanıtım ve reklam filmi projelerine başlarken ajansa verilecek en doğru brief şablonu, bütçe planlaması, hedef kitle tanımları ve format rehberi.",
    excerpt: "Başarılı bir video prodüksiyon projesinin %50'si sette değil, doğru yazılmış bir brief ile masada kazanılır. Zaman ve bütçe kaybını önleyen adım adım brief rehberi ve hazır şablon.",
    category: "Rehberler",
    readTime: "4 dk okuma",
    publishDate: "2024-05-18",
    author: {
      name: "WE24 Müşteri İlişkileri Departmanı",
      role: "Proje Yönetimi & Yapım Koordinasyonu"
    },
    coverImage: "/project-covers/tanitim-filmi-3b-fab.webp",
    featured: false,
    tags: ["Brief Şablonu", "Tanıtım Filmi", "Prodüksiyon Rehberi", "Müşteri Kılavuzu", "Bütçe Planlama", "Proje Yönetimi"],
    relatedProjectSlugs: ["tanitim-filmi-3b-fab-we24-agency-fabrika-reklam-filmi", "dr-light-kurumsal-tanitim-filmi"],
    relatedServiceSlugs: ["tanitim-filmi", "reklam-filmi"],
    sections: [
      {
        heading: "1. Brief Neden Bu Kadar Kritiktir?",
        subheading: "Ajans ile Marka Arasındaki Zihinsel Senkronizasyon",
        paragraphs: [
          "Birçok marka, bir tanıtım filmi yaptırmak istediğinde ajansa yalnızca 'Bize güzel ve etkileyici bir film yapın' talebiyle gelir. Ancak 'güzel' kavramı son derece özneldir. Kimi marka için hızlı tempolu bir klip etkileyiciyken, bir diğeri için ağırbaşlı ve kurumsal bir anlatım değerlidir.",
          "Net ve eksiksiz bir brief; gereksiz revizyon turlarını ortadan kaldırır, bütçenin doğru ekipmana kanalize edilmesini sağlar ve çekim gününün kusursuz geçmesini garanti eder."
        ]
      },
      {
        heading: "2. Kusursuz Bir Video Brief'inde Yer Alması Gereken 6 Temel Madde",
        subheading: "Adım Adım Brief Mimarisi",
        paragraphs: [
          "Ajansımıza ileteceğiniz brief dokümanında şu 6 başlığın net olarak tanımlanmış olması projenin başarısını ikiye katlar:"
        ],
        bulletPoints: [
          "1. Projenin Amacı ve Hedefi: Bu film neyi başarmalı? (Marka bilinirliği, yeni ürün lansmanı, ihracat satışları, halka arz güveni)",
          "2. Hedef Kitle: Filmi kim izleyecek? (B2B yabancı alıcılar, C-Level yöneticiler, Z kuşağı tüketiciler, mevcut çalışanlar)",
          "3. Temel Mesaj (Core Message): İzleyicinin aklında kalmasını istediğiniz tek bir cümle nedir?",
          "4. Dağıtım Mecraları ve Formatlar: Film nerelerde gösterilecek? (Web sitesi 16:9, Fuar LED ekranları, Instagram/TikTok 9:16 dikey, TV kuşağı)",
          "5. Ton ve Görsel Dünya (Tone of Voice): Dinamik mi, prestijli ve sinematik mi, teknik ve eğitici mi? Beğendiğiniz referans video linkleri.",
          "6. Takvim ve Teslim Tarihi: Filmin yayına girmesi gereken kesin tarih nedir?"
        ]
      },
      {
        heading: "3. Doğru Bütçeleme ve Beklenti Yönetimi",
        subheading: "Bütçeyi Önceden Paylaşmanın Stratejik Avantajı",
        paragraphs: [
          "Bütçe aralığınızı ajansla şeffaf bir şekilde paylaşmak, ajansın size 'yapılamayacak hayaller' yerine eldeki kaynakla en yüksek sinematik kaliteyi sunacak doğru kamera (RED/ARRI), ışık tırı, oyuncu kadrosu ve drone konfigürasyonunu tasarlamasını sağlar."
        ]
      }
    ],
    checklist: {
      title: "Hazır Tanıtım Filmi Brief Kontrol Listesi",
      items: [
        "Şirket özeti ve mevcut marka konumlandırması tanımlandı mı?",
        "Filmin ana hedefi ve başarı metriği netleştirildi mi?",
        "Çekim yapılması planlanan mekânlar ve lokasyon izinleri belirlendi mi?",
        "İstenen seslendirme dilleri (Türkçe, İngilizce vb.) ve altyazı ihtiyaçları listelendi mi?",
        "En az 2-3 adet beğendiğiniz referans video linki dokümana eklendi mi?"
      ]
    },
    faqs: [
      {
        question: "Referans video vermek yaratıcılığı kısıtlar mı?",
        answer: "Hayır. Aksine ajansın sizin estetik zevkinizi ve temponuzu hızlıca kavramasını sağlayarak özgün bir yaratıcı dünya kurmasına zemin hazırlar."
      },
      {
        question: "Brief yazmakta zorlanıyorsak ajans destek olur mu?",
        answer: "Kesinlikle. WE24 Agency proje yöneticileri projenin başında sizinle bir 'Brief Çalıştayı' düzenleyerek tüm bu soruları birlikte yanıtlar ve brief dokümanını sizin için oluşturur."
      }
    ]
  }
];
